import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixMediaSiteMediaV1FoldersServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'apps._base_domain_': [
      {
        srcPath: '/_api/wix-privatemedia-proxy-server',
        destPath: '/api',
      },
      {
        srcPath: '/_api/wix-privatemedia-proxy-server/v1',
        destPath: '/v1',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/site-media/v1',
        destPath: '/v1',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
    'platform.rise.ai': [
      {
        srcPath: '/_api/wix-privatemedia-proxy-server',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_media_folders';

/**
 * Creates a new folder in the Media Manager. <br/>
 *
 * Use the `parentFolderId` parameter to specify in which existing folder you want the new folder to be created.
 * If no folder is specified, the new folder is created in the `media-root` folder.
 */
export function createFolder(payload: object): RequestOptionsFactory<any> {
  function __createFolder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.site_media.v1.folder',
      method: 'POST' as any,
      methodFqn: 'com.wix.media.site_media.v1.FoldersService.CreateFolder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaSiteMediaV1FoldersServiceUrl({
        protoPath: '/v1/folders',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'folder.createdDate' },
              { path: 'folder.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createFolder;
}

/** Gets information from a specific folder in the Media Manager. */
export function getFolder(payload: object): RequestOptionsFactory<any> {
  function __getFolder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.site_media.v1.folder',
      method: 'GET' as any,
      methodFqn: 'com.wix.media.site_media.v1.FoldersService.GetFolder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaSiteMediaV1FoldersServiceUrl({
        protoPath: '/v1/folders/{folderId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'folder.createdDate' },
              { path: 'folder.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getFolder;
}

/** Retrieves a list of folders in the Media Manager. To retrieve a list of folders within a specific folder in the Media Manager, specify the specific folder's ID in the `parentFolderId` parameter. If no folder is specified, the method retrieves a list of folders within the root folder of the Media Manager. */
export function listFolders(payload: object): RequestOptionsFactory<any> {
  function __listFolders({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'sort.origin.latitude' },
          { path: 'sort.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.media.site_media.v1.folder',
      method: 'GET' as any,
      methodFqn: 'com.wix.media.site_media.v1.FoldersService.ListFolders',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaSiteMediaV1FoldersServiceUrl({
        protoPath: '/v1/folders',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'folders.createdDate' },
              { path: 'folders.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listFolders;
}

/**
 * Retrieves a list of up to 200 folders with the specified filtering, sorting, and cursor paging.
 *
 * If no parameters are specified, the method returns all folders in the `MEDIA_ROOT` folder.
 *
 * ## Defaults
 *
 * Search Folders has the following default setting, which you can override:
 * + Sorted by `updatedDate` in descending order.
 *
 * ## Filters
 *
 * When using filters for dates, you must use
 * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
 *
 * ## See also
 *
 * To learn about working with *Search* methods, see
 * [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) and
 * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/get-started/sorting-and-paging).
 */
export function searchFolders(payload: object): RequestOptionsFactory<any> {
  function __searchFolders({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'sort.origin.latitude' },
          { path: 'sort.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.media.site_media.v1.folder',
      method: 'POST' as any,
      methodFqn: 'com.wix.media.site_media.v1.FoldersService.SearchFolders',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaSiteMediaV1FoldersServiceUrl({
        protoPath: '/v1/folders/search',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'folders.createdDate' },
              { path: 'folders.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchFolders;
}

/**
 * Updates a folder. <br />
 *
 * You can use the `parentFolderId` parameter to move a folder from its current parent folder to a different parent folder.
 */
export function updateFolder(payload: object): RequestOptionsFactory<any> {
  function __updateFolder({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'folder.createdDate' }, { path: 'folder.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.media.site_media.v1.folder',
      method: 'PATCH' as any,
      methodFqn: 'com.wix.media.site_media.v1.FoldersService.UpdateFolder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaSiteMediaV1FoldersServiceUrl({
        protoPath: '/v1/folders/{folder.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'folder.createdDate' },
              { path: 'folder.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateFolder;
}

/**
 * Generates a URL for downloading a compressed file containing a specific folder in the Media Manager. <br/>
 *
 * The compressed file can contain sub-folders, and up to 1000 files.
 */
export function generateFolderDownloadUrl(
  payload: object
): RequestOptionsFactory<any> {
  function __generateFolderDownloadUrl({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.site_media.v1.folder',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.site_media.v1.FoldersService.GenerateFolderDownloadUrl',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaSiteMediaV1FoldersServiceUrl({
        protoPath: '/v1/folders/{folderId}/generate-download-url',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __generateFolderDownloadUrl;
}

/**
 * Temporarily deletes the specified folders from the Media Manager. <br/>
 *
 * The deleted folders are moved to the Media Manager's `trash-root` folder (trash bin) unless permanently deleted. To permanently delete folders, specify the `permanent` parameter with the value `true`. Permanently deleting folders isn't reversible, so make sure that the files in these folders aren't being used in a site or in any other way as the files will no longer be accessible.
 *
 * Note the following:
 * * When a folder is deleted, the files in that folder are deleted.
 * * The specified folders can be from different parent folders.
 * * Moving multiple folders at once is an asynchronous action, and may take time for the changes to appear in the Media Manager.
 * * Attempting to delete folders that are already in the trash bin doesn't result in an error.
 * * If a site contains files from a deleted media folder, the files still appear on the site as the deleted folder is still in the Media Manager (in the trash bin).
 * * You can also use the [Bulk Restore Folders From Trash Bin](https://dev.wix.com/docs/rest/assets/media/media-manager/folders/bulk-restore-folders-from-trash-bin) method to restore folders from the Media Manager's trash bin.
 */
export function bulkDeleteFolders(payload: object): RequestOptionsFactory<any> {
  function __bulkDeleteFolders({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.site_media.v1.folder',
      method: 'POST' as any,
      methodFqn: 'com.wix.media.site_media.v1.FoldersService.BulkDeleteFolders',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaSiteMediaV1FoldersServiceUrl({
        protoPath: '/v1/bulk/folders/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteFolders;
}

/** Restores the specified folders from the Media Manager's trash bin, and moves them to their original locations in the Media Manager. */
export function bulkRestoreFoldersFromTrashBin(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkRestoreFoldersFromTrashBin({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.site_media.v1.folder',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.site_media.v1.FoldersService.BulkRestoreFoldersFromTrashBin',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaSiteMediaV1FoldersServiceUrl({
        protoPath: '/v1/bulk/trash-bin/folders/restore',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkRestoreFoldersFromTrashBin;
}

/** Retrieves a list of folders in the Media Manager's trash bin. */
export function listDeletedFolders(
  payload: object
): RequestOptionsFactory<any> {
  function __listDeletedFolders({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'sort.origin.latitude' },
          { path: 'sort.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.media.site_media.v1.folder',
      method: 'GET' as any,
      methodFqn:
        'com.wix.media.site_media.v1.FoldersService.ListDeletedFolders',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaSiteMediaV1FoldersServiceUrl({
        protoPath: '/v1/trash-bin/folders',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'folders.createdDate' },
              { path: 'folders.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listDeletedFolders;
}

/**
 * Retrieves a list of folders in the Media Manager, given the provided [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging), filtering, and sorting.
 *
 * Up to 200 folders can be returned per request.
 *
 * To learn about working with *Query* methods, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) and
 * [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging)
 */
export function queryFolders(payload: object): RequestOptionsFactory<any> {
  function __queryFolders({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.site_media.v1.folder',
      method: 'POST' as any,
      methodFqn: 'com.wix.media.site_media.v1.FoldersService.QueryFolders',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaSiteMediaV1FoldersServiceUrl({
        protoPath: '/v1/folders/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'folders.createdDate' },
              { path: 'folders.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryFolders;
}
